import { Separator } from "@twilio-paste/core/separator";
import { graphql } from "gatsby";
import { ComponentsDefinitionsQuery, ComponentsEntityQuery, ComponentsMembersQuery } from "queries";
import React from "react";
import "../assets/styles/code.css";
import { getInheretedProperties } from "../helper";
import { TableOfContents } from "../shared/components/TableOfContents";
import { PrimaryLayout } from "../shared/layouts";
import { ComponentChildren } from "./components/ComponentChildren";
import { ComponentHeader } from "./components/ComponentHeader";
import { ComponentMembers } from "./components/ComponentMembers";
import { ComponentProperties } from "./components/ComponentProperties";
import { Container } from "./components/Container";
import { ContentContainer } from "./components/ContentContainer";
import { HeadingContainer } from "./components/HeadingContainer";
import { RightContainer } from "./components/RightContainer";
import { SeparatorContainer } from "./components/SeparatorContainer";

export const queryFragments = graphql`
    fragment DefinitionsQueryFragment on DocsJson {
        kind
        comment
        name
        type {
            names
        }
        ...Property
    }
`;
export const query = graphql`
    query ComponentTypeDefinitionsQuery($name: String, $props: String, $propsChildren: String) {
        entityQuery: allDocsJson(filter: { name: { eq: $name }, kind: { eq: "class" }, undocumented: { ne: true } }) {
            edges {
                node {
                    name
                    description
                    classdesc
                    tags {
                        originalTitle
                        title
                        text
                    }
                    component {
                        displayName
                        param
                        type
                        children {
                            key
                            condition
                            description
                            type
                            align
                        }
                    }
                    type {
                        names
                    }
                }
            }
        }
        definitionsQuery: allDocsJson(
            filter: { memberof: { eq: $name }, kind: { eq: "typedef" }, name: { eq: $props } }
        ) {
            edges {
                node {
                    ...DefinitionsQueryFragment
                }
            }
        }
        childrenDefinitionsQuery: allDocsJson(
            filter: { memberof: { eq: $name }, kind: { eq: "typedef" }, name: { eq: $propsChildren } }
        ) {
            edges {
                node {
                    ...DefinitionsQueryFragment
                }
            }
        }
        allDefinitionsQuery: allDocsJson(filter: { kind: { eq: "typedef" } }) {
            edges {
                node {
                    ...DefinitionsQueryFragment
                }
            }
        }
        membersQuery: allDocsJson(
            filter: { memberof: { eq: $name }, kind: { eq: "member" }, undocumented: { ne: true } }
        ) {
            edges {
                node {
                    kind
                    description
                    comment
                    name
                    scope
                    readonly
                    ...Type
                }
            }
        }
    }
`;

const getHeadings = (
    isComponentChildren: boolean,
    isComponentMembers: boolean,
    isComponentProperties: boolean,
    isComponentChildrenProperties: boolean
) => {
    const headings = [];
    if (isComponentChildren) {
        headings.push({
            name: "Component Children",
            slug: "component-children",
            depth: 2
        });
    }
    if (isComponentMembers) {
        headings.push({
            name: "Static Properties",
            slug: "static-properties",
            depth: 2
        });
    }
    if (isComponentProperties) {
        headings.push({
            name: "Component Properties",
            slug: "component-properties",
            depth: 2
        });
    }
    if (isComponentChildrenProperties) {
        headings.push({
            name: "Component Children Properties",
            slug: "component-children-properties",
            depth: 2
        });
    }
    return headings;
};

export default function ProgrammableComponent(props: {
    data: {
        entityQuery: ComponentsEntityQuery;
        membersQuery: ComponentsMembersQuery;
        definitionsQuery: ComponentsDefinitionsQuery;
        childrenDefinitionsQuery: ComponentsDefinitionsQuery;
        allDefinitionsQuery: ComponentsDefinitionsQuery;
    };
}) {
    const { entityQuery, membersQuery, definitionsQuery, childrenDefinitionsQuery, allDefinitionsQuery } = props.data;

    const [entity] = entityQuery.edges.map(({ node }) => node);
    const members = membersQuery.edges.map(({ node }) => node);
    const properties = definitionsQuery.edges.map(({ node }) => node)[0]?.properties || [];
    const childrenProperties = childrenDefinitionsQuery.edges.map(({ node }) => node)[0]?.properties || [];

    properties.push(...getInheretedProperties(definitionsQuery.edges?.[0]?.node, allDefinitionsQuery));
    childrenProperties.push(...getInheretedProperties(childrenDefinitionsQuery.edges?.[0]?.node, allDefinitionsQuery));

    const headings = getHeadings(
        !!entity?.component?.children?.length,
        !!members?.length,
        !!properties?.length,
        !!childrenProperties.length
    );
    return (
        <PrimaryLayout>
            <Container>
                <HeadingContainer>
                    <ComponentHeader entity={entity} />
                </HeadingContainer>
                <SeparatorContainer>
                    <Separator orientation="horizontal" verticalSpacing="space100" />
                </SeparatorContainer>
                <ContentContainer overflow="hidden">
                    <ComponentChildren component={entity.component} />
                    <ComponentMembers members={members} />
                    <ComponentProperties
                        properties={properties}
                        heading="Component Properties"
                        uniqueName="component-properties"
                    />
                    <ComponentProperties
                        properties={childrenProperties}
                        heading="Component Children Properties"
                        uniqueName="component-children-properties"
                    />
                </ContentContainer>
                <RightContainer>
                    <TableOfContents headings={headings} />
                </RightContainer>
            </Container>
        </PrimaryLayout>
    );
}
